import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Plane, ArrowRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { FileUploadButton } from "@/components/FileUploadButton";
import { TravelerAssign, TravelerChips } from "@/components/TravelerAssign";
import { formatError } from "@/lib/api";
import { useCollection } from "./useCollection";

const empty = { airline: "", flight_number: "", from_airport: "", to_airport: "", depart_at: "", arrive_at: "", confirmation: "", seat: "", ticket_file_id: null, traveler_ids: [], notes: "" };

export default function FlightsSection({ tripId, onChange }) {
  const { items, loading, create, update, remove } = useCollection(tripId, "flights", onChange);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openNew() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(item) {
    setEditingId(item.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in empty)) });
    setOpen(true);
  }
  function handleOpenChange(v) { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }

  async function save() {
    if (!form.airline || !form.flight_number || !form.from_airport || !form.to_airport) {
      toast.error("Airline, flight number, from & to are required"); return;
    }
    setSaving(true);
    try {
      if (editingId) { await update(editingId, form); toast.success("Flight updated"); }
      else { await create(form); toast.success("Flight added"); }
      handleOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-xl">Flights</h3><p className="text-sm text-stone-500">Boarding passes and reservations.</p></div>
        <Button className="rounded-full bg-primary text-primary-foreground" onClick={openNew} data-testid="add-flight-btn"><Plus className="w-4 h-4 mr-2" />Add flight</Button>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit flight" : "New flight"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Airline</Label><Input className="rounded-xl" value={form.airline} onChange={(e) => setForm({ ...form, airline: e.target.value })} data-testid="flight-airline-input" /></div>
              <div><Label>Flight #</Label><Input className="rounded-xl" value={form.flight_number} onChange={(e) => setForm({ ...form, flight_number: e.target.value })} data-testid="flight-number-input" /></div>
              <div><Label>From</Label><Input className="rounded-xl" placeholder="JFK" value={form.from_airport} onChange={(e) => setForm({ ...form, from_airport: e.target.value })} data-testid="flight-from-input" /></div>
              <div><Label>To</Label><Input className="rounded-xl" placeholder="LIS" value={form.to_airport} onChange={(e) => setForm({ ...form, to_airport: e.target.value })} data-testid="flight-to-input" /></div>
              <div><Label>Departs</Label><Input type="text" placeholder="YYYY-MM-DD HH:MM" title="Format: YYYY-MM-DD HH:MM" className="rounded-xl" value={form.depart_at || ""} onChange={(e) => setForm({ ...form, depart_at: e.target.value })} /></div>
              <div><Label>Arrives</Label><Input type="text" placeholder="YYYY-MM-DD HH:MM" title="Format: YYYY-MM-DD HH:MM" className="rounded-xl" value={form.arrive_at || ""} onChange={(e) => setForm({ ...form, arrive_at: e.target.value })} /></div>
              <div><Label>Confirmation</Label><Input className="rounded-xl" value={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} /></div>
              <div><Label>Seat</Label><Input className="rounded-xl" value={form.seat} onChange={(e) => setForm({ ...form, seat: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3"><FileUploadButton label={form.ticket_file_id ? "Change ticket" : "Attach ticket"} testId="flight-ticket-upload" onUploaded={(id) => setForm((f) => ({ ...f, ticket_file_id: id }))} />{form.ticket_file_id && <span className="text-xs text-stone-500">Attached</span>}</div>
              <div className="col-span-2 pt-1"><TravelerAssign tripId={tripId} value={form.traveler_ids} onChange={(ids) => setForm((f) => ({ ...f, traveler_ids: ids }))} testId="flight-travelers" /></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">Cancel</Button><Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-flight-btn">{saving ? "Saving..." : editingId ? "Save changes" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="h-32 rounded-2xl bg-muted animate-pulse" /> :
       items.length === 0 ? (
        <Card className="rounded-2xl p-10 border-dashed border-border text-center" data-testid="empty-flights"><Plane className="w-8 h-8 mx-auto text-primary" /><p className="mt-2 text-stone-500">No flights yet.</p></Card>
       ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((f) => (
            <Card key={f.id} className="p-5 rounded-2xl border-border" data-testid={`flight-${f.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary"><Plane className="w-4 h-4" /><span className="font-medium">{f.airline}</span><span className="text-stone-500">· {f.flight_number}</span></div>
                <div className="flex items-center gap-0.5">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(f)} className="rounded-full text-stone-400 hover:text-primary" data-testid={`edit-flight-${f.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(f.id)} className="rounded-full text-stone-400 hover:text-destructive" data-testid={`delete-flight-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="text-2xl font-display">{f.from_airport}</div>
                <ArrowRight className="w-4 h-4 text-stone-400" />
                <div className="text-2xl font-display">{f.to_airport}</div>
              </div>
              <div className="mt-2 text-xs text-stone-500 grid grid-cols-2 gap-1">
                {f.depart_at && <div>Dep: {f.depart_at}</div>}
                {f.arrive_at && <div>Arr: {f.arrive_at}</div>}
                {f.confirmation && <div>Conf: {f.confirmation}</div>}
                {f.seat && <div>Seat: {f.seat}</div>}
              </div>
              {f.ticket_file_id && <AuthImage fileId={f.ticket_file_id} className="mt-3 w-full h-32 object-cover rounded-xl" />}
              <TravelerChips tripId={tripId} travelerIds={f.traveler_ids} />
            </Card>
          ))}
        </div>
       )}
    </div>
  );
}
